// src/components/ProjectSection/ProjectsSection.js
import React, { useState } from "react";
import { Box, Container, Typography, Slider, IconButton } from "@mui/material";
import { styled } from "@mui/system";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * ✅ Performance-only additions (NO UI/originality changes):
 * 1) Preload current + next 2 + prev 2 slide images (before+after)
 * 2) Wait to switch slide until target images are ready (prevents black flash)
 * 3) Warm-cache neighbors on every index change
 * 4) Idle-time warm preload of ALL slides after first paint (small list = smoothest UX)
 * 5) Use eager/high priority only for active slide, lazy for others
 */

/** small in-memory cache to avoid re-preloading same src */
const __imgCache = new Set();

function preloadImage(src) {
	if (!src) return Promise.resolve();
	if (__imgCache.has(src)) return Promise.resolve();

	return new Promise((resolve) => {
		const img = new Image();
		img.decoding = "async";
		img.loading = "eager";
		img.onload = () => {
			__imgCache.add(src);
			resolve();
		};
		img.onerror = () => resolve(); // never block UX
		img.src = src;

		// if already cached by the browser
		if (img.complete) {
			__imgCache.add(src);
			resolve();
		}
	});
}

const CompareSlider = styled(Slider)(({ theme }) => ({
	position: "absolute",
	left: 0,
	top: "50%",
	transform: "translateY(-50%)",
	width: "100%",
	zIndex: 5,
	color: "transparent",
	// root should not look clickable everywhere
	cursor: "default",
	"& .MuiSlider-rail": {
		opacity: 0,
	},
	"& .MuiSlider-track": {
		opacity: 0,
	},
	"& .MuiSlider-thumb": {
		width: 30,
		height: 30,
		borderRadius: 6,
		backgroundColor: "#674934",
		border: "3px solid #fff",
		boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
		cursor: "pointer", // only thumb looks draggable
	},
	[theme.breakpoints.down("sm")]: {
		"& .MuiSlider-thumb": {
			width: 24,
			height: 24,
			borderRadius: 5,
		},
	},
}));

function ProjectSlide({ project, isActive }) {
	const [sliderValue, setSliderValue] = useState(50);

	// ✅ Only allow drag when pointer starts on the thumb
	const blockIfNotThumb = (event) => {
		const root = event.currentTarget;
		const thumb = root.querySelector(".MuiSlider-thumb");

		if (!thumb) return;

		// if the actual target is NOT inside the thumb, block the slider logic
		if (!thumb.contains(event.target)) {
			event.stopPropagation();
			event.preventDefault();
		}
	};

	return (
		<Box
			sx={{
				flex: "0 0 100%",
				maxWidth: "100%",
				boxSizing: "border-box",
				position: "relative",
			}}
		>
			<Box
				sx={{
					position: "relative",
					width: "100%",
					maxWidth: { xs: "100%", md: 1500 }, // full width on mobile
					mx: { xs: 0, md: "auto" }, // no side margin on mobile
					borderRadius: 2,
					overflow: "hidden",
					height: { xs: 220, sm: 300, md: 550, lg: 590 },
					backgroundColor: "#000",
				}}
			>
				{/* BEFORE image – full background (base) */}
				<Box
					component="img"
					src={project.before}
					alt="Before"
					loading={isActive ? "eager" : "lazy"}
					decoding="async"
					fetchpriority={isActive ? "high" : "auto"}
					sx={{
						position: "absolute",
						inset: 0,
						width: "100%",
						height: "100%",
						objectFit: "cover",
						zIndex: 1,
					}}
				/>

				{/* AFTER image – overlay, revealed from left as slider moves */}
				<Box
					component="img"
					src={project.after}
					alt="After"
					loading={isActive ? "eager" : "lazy"}
					decoding="async"
					fetchpriority={isActive ? "high" : "auto"}
					sx={{
						position: "absolute",
						inset: 0,
						width: "100%",
						height: "100%",
						objectFit: "cover",
						zIndex: 2,
						clipPath: `polygon(0 0, ${sliderValue}% 0, ${sliderValue}% 100%, 0 100%)`,
					}}
				/>

				{/* Divider line */}
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: `${sliderValue}%`,
						transform: "translateX(-50%)",
						width: 4,
						height: "100%",
						backgroundColor: "#D5C1AD",
						zIndex: 3,
					}}
				/>

				{/* Slider control */}
				<CompareSlider
					min={0}
					max={100}
					value={sliderValue}
					onChange={(_, value) => setSliderValue(value)}
					// 🧲 block clicks/taps on image area – only allow thumb drags
					onMouseDownCapture={blockIfNotThumb}
					onTouchStartCapture={blockIfNotThumb}
				/>

				{/* Slide pill */}
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: `${sliderValue}%`,
						transform: "translate(-50%, -50%)",
						backgroundColor: "#674934",
						color: "#fff",
						fontFamily: "Source Sans Pro, sans-serif",
						fontSize: 14,
						px: 2.5,
						py: 0.75,
						borderRadius: 999,
						zIndex: 7,
						whiteSpace: "nowrap",
						pointerEvents: "none",
					}}
				>
					Slide
				</Box>

				{/* Before label */}
				<Typography
					sx={{
						position: "absolute",
						top: 12,
						left: 12,
						fontFamily: "Source Sans Pro, sans-serif",
						fontSize: 14,
						fontWeight: 600,
						color: "#674934",
						backgroundColor: "#FFFFFF",
						px: 1.75,
						py: 0.5,
						borderRadius: 999,
						zIndex: 6,
						boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
					}}
				>
					Before
				</Typography>

				{/* After label */}
				<Typography
					sx={{
						position: "absolute",
						top: 12,
						right: 12,
						fontFamily: "Source Sans Pro, sans-serif",
						fontSize: 14,
						fontWeight: 600,
						color: "#674934",
						backgroundColor: "#FFFFFF",
						px: 1.75,
						py: 0.5,
						borderRadius: 999,
						zIndex: 6,
						boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
					}}
				>
					After
				</Typography>

				{/* Project info chip */}
				<Box
					sx={{
						position: "absolute",
						bottom: 16,
						left: 16,
						backgroundColor: "rgba(103,73,52,1)",
						color: "#fff",
						fontFamily: "Source Sans Pro, sans-serif",
						fontSize: 14,
						fontWeight: 400,
						px: 2,
						py: 1,
						borderRadius: 1.5,
						zIndex: 6,
					}}
				>
					{project.info.split("\n").map((line, i) => (
						<Box key={i}>{line}</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
}

function ProjectsSection() {
	const projects = [
		{
			after: "images/projects-section/before11.jpg",
			before: "images/projects-section/after1.webp",
			info: "Skye 20, Vijay nagar",
		},
		{
			after: "images/projects-section/before4.png",
			before: "images/projects-section/after4.webp",
			info: "Skye 20, Vijay nagar",
		},
		{
			after: "images/projects-section/before23.jpg",
			before: "images/projects-section/after2.webp",
			info: "Sahil Infra office",
		},
		{
			after: "images/projects-section/before5.png",
			before: "images/projects-section/after5.webp",
			info: "Shangrila",
		},
	];

	const [currentProjectIndex, setCurrentProjectIndex] = useState(0);

	/** ✅ Added: prevents double-click spamming while we ensure target is ready */
	const isNavigatingRef = React.useRef(false);

	/** ✅ Added: warm preload around an index (next 2 + prev 2) */
	const warmAroundIndex = React.useCallback(
		(index) => {
			const n = projects.length;
			if (!n) return;

			const indices = [
				(index + 1) % n,
				(index + 2) % n,
				(index - 1 + n) % n,
				(index - 2 + n) % n,
			];

			indices.forEach((i) => {
				preloadImage(projects[i]?.before);
				preloadImage(projects[i]?.after);
			});
		},
		[projects]
	);

	/** ✅ Added: on mount, warm current neighbors + idle preload all (small carousel = best UX) */
	React.useEffect(() => {
		// current neighbors right away
		warmAroundIndex(currentProjectIndex);

		// after first paint, preload ALL slides in idle time so switching is instant
		const run = () => {
			projects.forEach((p) => {
				preloadImage(p.before);
				preloadImage(p.after);
			});
		};

		let idleId;
		if (typeof window !== "undefined" && "requestIdleCallback" in window) {
			idleId = window.requestIdleCallback(run, { timeout: 1500 });
		} else {
			idleId = setTimeout(run, 800);
		}

		return () => {
			if (typeof window !== "undefined" && "cancelIdleCallback" in window) {
				window.cancelIdleCallback(idleId);
			} else {
				clearTimeout(idleId);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/** ✅ Added: also warm neighbors whenever index changes */
	React.useEffect(() => {
		warmAroundIndex(currentProjectIndex);
	}, [currentProjectIndex, warmAroundIndex]);

	/** ✅ Added: switch only after target images are ready (prevents black flash completely) */
	const goToProjectSlide = async (index) => {
		if (index === currentProjectIndex) return;
		if (isNavigatingRef.current) return;

		isNavigatingRef.current = true;

		// Ensure target slide is ready before switching
		await Promise.all([
			preloadImage(projects[index]?.before),
			preloadImage(projects[index]?.after),
		]);

		setCurrentProjectIndex(index);

		// warm next/prev around the new index
		warmAroundIndex(index);

		isNavigatingRef.current = false;
	};

	const goToPrevious = async () => {
		const target =
			currentProjectIndex === 0 ? projects.length - 1 : currentProjectIndex - 1;
		await goToProjectSlide(target);
	};

	const goToNext = async () => {
		const target =
			currentProjectIndex === projects.length - 1 ? 0 : currentProjectIndex + 1;
		await goToProjectSlide(target);
	};

	return (
		<Container
			maxWidth={false}
			sx={{
				width: { xs: "100%", md: "95%" }, // full width on mobile
				maxWidth: 1500,
				mx: "auto",
				px: { xs: 1, md: 0 }, // small horizontal padding on mobile
				my: { xs: 4, md: 6 },
				textAlign: "left",
				position: "relative",
				overflow: "hidden",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: { xs: "flex-start", md: "center" },
					mb: 3,
					gap: 2,
				}}
			>
				<Box>
					<Typography
						sx={{
							fontFamily: "Source Sans Pro, sans-serif",
							fontWeight: 300,
							fontSize: 16,
							lineHeight: "24px",
							letterSpacing: "2px",
							color: "#674934",
							textTransform: "uppercase",
						}}
					>
						Projects
					</Typography>
					<Typography
						sx={{
							fontFamily: "Lora, serif",
							fontWeight: 500,
							fontSize: { xs: 28, md: 44 },
							lineHeight: { xs: "34px", md: "50px" },
							color: "#674934",
							mt: 1,
						}}
					>
						Transforming Spaces
					</Typography>
					<Typography
						sx={{
							fontFamily: "Source Sans Pro, sans-serif",
							fontWeight: 400,
							fontSize: { xs: 13, md: 18 },
							lineHeight: { xs: "18px", md: "24px" },
							color: "#674934",
							mt: 0.5,
						}}
					>
						Explore our portfolio of beautifully crafted interior designs
					</Typography>
				</Box>

				{/* Dots on the right (tablet/desktop only) */}
				<Box
					sx={{
						display: { xs: "none", md: "flex" },
						alignItems: "center",
						gap: 1,
						mt: { xs: 2, md: 3.5 },
					}}
				>
					{projects.map((_, index) => (
						<Box
							key={index}
							onClick={() => goToProjectSlide(index)}
							sx={{
								width: 12,
								height: 12,
								borderRadius: "50%",
								backgroundColor: "#674934",
								opacity: index === currentProjectIndex ? 1 : 0.4,
								cursor: "pointer",
								transition: "opacity 0.3s ease",
							}}
						/>
					))}
				</Box>
			</Box>

			{/* Slides */}
			<Box sx={{ width: "100%", overflow: "hidden", position: "relative" }}>
				{/* Left Arrow Button */}
				<Box
					component={IconButton}
					onClick={goToPrevious}
					sx={{
						position: "absolute",
						left: { xs: 8, sm: 12, md: 16 },
						top: "50%",
						transform: "translateY(-50%)",
						zIndex: 10,
						backgroundColor: "#674934",
						color: "#FFFFFF",
						width: { xs: 40, md: 50 },
						height: { xs: 40, md: 50 },
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						borderRadius: "50%",
						transition: "all 0.3s ease",
						"&:hover": {
							backgroundColor: "#533A29",
							transform: "translateY(-50%) scale(1.1)",
						},
						"& svg": {
							fontSize: { xs: 24, md: 32 },
						},
					}}
				>
					<ChevronLeftIcon />
				</Box>

				{/* Carousel Container */}
				<Box
					sx={{
						display: "flex",
						width: "100%",
						transition: "transform 0.5s ease-in-out",
						transform: `translateX(-${currentProjectIndex * 100}%)`,
					}}
				>
					{projects.map((project, index) => (
						<ProjectSlide
							key={index}
							project={project}
							isActive={index === currentProjectIndex}
						/>
					))}
				</Box>

				{/* Right Arrow Button */}
				<Box
					component={IconButton}
					onClick={goToNext}
					sx={{
						position: "absolute",
						right: { xs: 8, sm: 12, md: 16 },
						top: "50%",
						transform: "translateY(-50%)",
						zIndex: 10,
						backgroundColor: "#674934",
						color: "#FFFFFF",
						width: { xs: 40, md: 50 },
						height: { xs: 40, md: 50 },
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						borderRadius: "50%",
						transition: "all 0.3s ease",
						"&:hover": {
							backgroundColor: "#533A29",
							transform: "translateY(-50%) scale(1.1)",
						},
						"& svg": {
							fontSize: { xs: 24, md: 32 },
						},
					}}
				>
					<ChevronRightIcon />
				</Box>
			</Box>

			{/* Dots under card on mobile */}
			<Box
				sx={{
					display: { xs: "flex", md: "none" },
					justifyContent: "center",
					alignItems: "center",
					gap: 1,
					mt: 2,
				}}
			>
				{projects.map((_, index) => (
					<Box
						key={index}
						onClick={() => goToProjectSlide(index)}
						sx={{
							width: 10,
							height: 10,
							borderRadius: "50%",
							backgroundColor: "#674934",
							opacity: index === currentProjectIndex ? 1 : 0.4,
							cursor: "pointer",
							transition: "opacity 0.3s ease",
						}}
					/>
				))}
			</Box>
		</Container>
	);
}

export default ProjectsSection;
